"use client";

import { useState, useEffect } from "react";
import type { Specialist } from "@/entities/specialist";
import { TIMEZONES } from "@/shared/config";

interface FormData {
  name: string;
  phone: string;
  email: string;
  preferredTime: string;
  timezone: string;
  contactMethod: string;
  city: string;
  specialist: string;
  comments: string;
  privacyAccepted: boolean;
  marketingAccepted: boolean;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
  contactMethod?: string;
  privacyAccepted?: string;
}

interface DetailedContactFormProps {
  mode?: "default" | "inverted";
  specialists?: Specialist[];
}

export function DetailedContactForm({
  mode = "default",
  specialists = [],
}: DetailedContactFormProps) {
  const inv = mode === "inverted";

  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "+7",
    email: "",
    preferredTime: "any",
    timezone: "Europe/Moscow",
    contactMethod: "phone",
    city: "",
    specialist: "any",
    comments: "",
    privacyAccepted: false,
    marketingAccepted: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [timezoneDetected, setTimezoneDetected] = useState(false);

  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isKnown = TIMEZONES.some((t) => t.value === tz);
      setFormData((prev) => ({
        ...prev,
        timezone: isKnown ? tz : "Europe/Moscow",
      }));
      setTimezoneDetected(isKnown);
    } catch {
      setTimezoneDetected(false);
    }
  }, []);

  const formatPhone = (value: string) => {
    const phone = value.replace(/\D/g, "");
    if (phone.length <= 1) return "+7 (";
    let f = "+7 (";
    if (phone.length > 1) f += phone.substring(1, 4);
    if (phone.length >= 5) f += `) ${phone.substring(4, 7)}`;
    if (phone.length >= 8) f += `-${phone.substring(7, 9)}`;
    if (phone.length >= 10) f += `-${phone.substring(9, 11)}`;
    return f;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (errors.phone) setErrors({ ...errors, phone: undefined });
    if (value === "+7 ") {
      setFormData({ ...formData, phone: "+7" });
      return;
    }
    if (!value.startsWith("+7")) value = "+7";
    setFormData({ ...formData, phone: formatPhone(value) });
  };

  const getPhoneMaskElements = (value: string) => {
    const phone = value.replace(/\D/g, "");
    const maskCls = inv ? "text-white/30" : "text-foreground/30";
    if (phone.length === 0)
      return <span className={maskCls}>+7 (___) ___-__-__</span>;
    let fullMask = value;
    const tmpl = "+7 (___) ___-__-__";
    if (fullMask.length < tmpl.length)
      fullMask += tmpl.substring(fullMask.length);
    return (
      <>
        <span className="opacity-0">{fullMask.substring(0, value.length)}</span>
        <span className={maskCls}>
          {fullMask.substring(value.length)}
        </span>
      </>
    );
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (formData.phone === "+7" || formData.phone === "+7 ") {
      setFormData({ ...formData, phone: "+7 (" });
      setTimeout(() => e.target.setSelectionRange(4, 4), 0);
    } else if (formData.phone === "+7 (") {
      setTimeout(() => e.target.setSelectionRange(4, 4), 0);
    }
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!formData.name.trim())
      e.name = "Напишите, пожалуйста, как к вам обращаться";
    if (formData.phone.replace(/\D/g, "").length < 11)
      e.phone = "Кажется, в номере не хватает цифр";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = "Пожалуйста, введите корректный email";
    if (!formData.privacyAccepted)
      e.privacyAccepted = "Нужно ваше согласие на обработку данных";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNameBlur = () => {
    if (!formData.name.trim())
      setErrors((p) => ({
        ...p,
        name: "Напишите, пожалуйста, как к вам обращаться",
      }));
  };
  const handlePhoneBlur = () => {
    const d = formData.phone.replace(/\D/g, "");
    if (d.length > 1 && d.length < 11)
      setErrors((p) => ({ ...p, phone: "Кажется, в номере не хватает цифр" }));
    else if (d.length <= 1)
      setErrors((p) => ({
        ...p,
        phone: "Напишите, пожалуйста, номер, чтобы мы могли связаться",
      }));
  };
  const handleEmailBlur = () => {
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      setErrors((p) => ({
        ...p,
        email: "Пожалуйста, введите корректный email",
      }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsSubmitting(false);
    setIsSuccess(true);
  };

  // Derived class helpers
  const LABEL_CLS = inv
    ? "text-xs uppercase tracking-widest text-white/60 font-content"
    : "text-xs uppercase tracking-widest text-foreground/40 font-content";

  const FIELD_CLS = inv
    ? "w-full bg-transparent border-0 border-b pb-2 text-white text-fluid-base placeholder:text-white/30 focus:outline-none transition-colors duration-300 border-white/30 focus:border-white"
    : "w-full bg-transparent border-0 border-b pb-2 text-foreground text-fluid-base placeholder:text-foreground/30 focus:outline-none transition-colors duration-300 border-border/40 focus:border-help";

  const FIELD_ERROR_CLS = inv
    ? "border-white/70"
    : "border-[var(--media,orange)]";

  const CHEVRON = (
    <div className={`pointer-events-none absolute bottom-2 right-0 ${inv ? "text-white/40" : "text-foreground/40"}`}>
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>
  );

  const OPT_CLS = inv ? "bg-[#047a73]" : "bg-background";
  const ERR_CLS = inv
    ? "text-xs text-red-300 mt-1"
    : "text-xs text-[var(--destructive)] mt-1";
  const CHECKBOX_LABEL_CLS = inv
    ? "text-xs text-white/70 leading-relaxed cursor-pointer"
    : "text-xs text-foreground/60 leading-relaxed cursor-pointer";
  const LINK_CLS = inv
    ? "text-white underline hover:text-white/80"
    : "text-help hover:underline";
  const HINT_CLS = inv
    ? "text-xs text-white/40 mt-1"
    : "text-xs text-foreground/40 mt-1";
  const REQ_STAR = inv
    ? "text-white/60"
    : "text-[var(--media,orange)]";
  const BTN_CLS = inv
    ? "h-[44px] rounded-full bg-transparent !text-white px-6 flex items-center justify-center font-content font-semibold text-[var(--fluid-text-sm)] tracking-wide cursor-pointer transition-all duration-300 select-none border-[1.5px] border-white w-full disabled:opacity-40 disabled:pointer-events-none lg:hover:bg-white lg:hover:!text-help"
    : "btn-light text-[var(--help)] w-full lg:hover:bg-[var(--help)] lg:hover:!text-white disabled:opacity-40 disabled:pointer-events-none";

  // Button is active only when required fields are filled
  const isFormValid =
    formData.name.trim() !== "" &&
    formData.phone.replace(/\D/g, "").length >= 11 &&
    formData.privacyAccepted;

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-4 p-4">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-2 ${inv ? "bg-white/20 text-white" : "bg-help/10 text-help"}`}>
          ✓
        </div>
        <h3 className={`font-heading text-fluid-xl ${inv ? "text-white" : "text-foreground"}`}>
          Заявка отправлена 🎉
        </h3>
        <p className={`font-content ${inv ? "text-white/80" : "text-foreground/80"}`}>
          Спасибо! Мы свяжемся с вами в ближайшее время выбранным способом.
        </p>
        <button
          onClick={() => {
            setIsSuccess(false);
            setFormData({
              name: "",
              phone: "+7",
              email: "",
              preferredTime: "any",
              timezone: "Europe/Moscow",
              contactMethod: "phone",
              city: "",
              specialist: "any",
              comments: "",
              privacyAccepted: false,
              marketingAccepted: false,
            });
          }}
          className={BTN_CLS + " mt-fluid-md"}
        >
          Отправить еще
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col w-full">
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-name" className={LABEL_CLS}>
              Как к вам обращаться{" "}
              <span className={REQ_STAR}>*</span>
            </label>
            <input
              type="text"
              id="dcf-name"
              name="name"
              autoComplete="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              onBlur={handleNameBlur}
              placeholder="Ваше имя"
              className={`${FIELD_CLS} ${errors.name ? FIELD_ERROR_CLS : ""}`}
            />
            {errors.name && (
              <span className={ERR_CLS}>{errors.name}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-phone" className={LABEL_CLS}>
              Телефон <span className={REQ_STAR}>*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                id="dcf-phone"
                name="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                onFocus={handlePhoneFocus}
                onBlur={handlePhoneBlur}
                onClick={(e) => {
                  const t = e.target as HTMLInputElement;
                  if (t.selectionStart !== null && t.selectionStart < 4)
                    t.setSelectionRange(4, 4);
                }}
                onKeyDown={(e) => {
                  const t = e.target as HTMLInputElement;
                  if (
                    (e.key === "Backspace" || e.key === "ArrowLeft") &&
                    t.selectionStart !== null &&
                    t.selectionStart <= 4
                  )
                    e.preventDefault();
                }}
                maxLength={18}
                className={`${FIELD_CLS} relative z-10 ${errors.phone ? FIELD_ERROR_CLS : ""}`}
              />
              <span className={`absolute bottom-2 left-0 pointer-events-none z-0 text-fluid-base flex whitespace-pre ${inv ? "text-white/30" : "text-foreground/30"}`}>
                {getPhoneMaskElements(formData.phone)}
              </span>
            </div>
            {errors.phone && (
              <span className={ERR_CLS}>{errors.phone}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-email" className={LABEL_CLS}>
              Email
            </label>
            <input
              type="email"
              id="dcf-email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              onBlur={handleEmailBlur}
              placeholder="Ваш email"
              className={`${FIELD_CLS} ${errors.email ? FIELD_ERROR_CLS : ""}`}
            />
            {errors.email && (
              <span className={ERR_CLS}>{errors.email}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-city" className={LABEL_CLS}>
              Город
            </label>
            <input
              type="text"
              id="dcf-city"
              name="city"
              value={formData.city}
              onChange={(e) =>
                setFormData({ ...formData, city: e.target.value })
              }
              placeholder="Ваш город"
              className={FIELD_CLS}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-contactMethod" className={LABEL_CLS}>
              Способ связи
            </label>
            <div className="relative">
              <select
                id="dcf-contactMethod"
                value={formData.contactMethod}
                onChange={(e) =>
                  setFormData({ ...formData, contactMethod: e.target.value })
                }
                className={`${FIELD_CLS} appearance-none pr-6`}
              >
                <option value="phone" className={OPT_CLS}>
                  Обратный звонок
                </option>
                <option value="telegram" className={OPT_CLS}>
                  Телеграм
                </option>
                <option value="whatsapp" className={OPT_CLS}>
                  WhatsApp
                </option>
                <option value="max" className={OPT_CLS}>
                  Мессенджер MAX
                </option>
              </select>
              {CHEVRON}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="dcf-specialist" className={LABEL_CLS}>
              Специалист
            </label>
            <div className="relative">
              <select
                id="dcf-specialist"
                value={formData.specialist}
                onChange={(e) =>
                  setFormData({ ...formData, specialist: e.target.value })
                }
                className={`${FIELD_CLS} appearance-none pr-6`}
              >
                <option value="any" className={OPT_CLS}>
                  Подобрать специалиста
                </option>
                {(specialists ?? []).map((spec) => (
                  <option
                    key={spec.id}
                    value={spec.id.toString()}
                    className={OPT_CLS}
                  >
                    {spec.name}
                  </option>
                ))}
              </select>
              {CHEVRON}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="dcf-preferredTime" className={LABEL_CLS}>
            Во сколько можно позвонить
          </label>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="relative flex-1">
              <select
                id="dcf-preferredTime"
                value={formData.preferredTime}
                onChange={(e) =>
                  setFormData({ ...formData, preferredTime: e.target.value })
                }
                className={`${FIELD_CLS} appearance-none pr-6`}
              >
                <option value="any" className={OPT_CLS}>
                  В любое время
                </option>
                <option value="morning" className={OPT_CLS}>
                  Утром (9:00 — 12:00)
                </option>
                <option value="afternoon" className={OPT_CLS}>
                  Днем (12:00 — 18:00)
                </option>
                <option value="evening" className={OPT_CLS}>
                  Вечером (18:00 — 21:00)
                </option>
              </select>
              {CHEVRON}
            </div>
            <div className="relative sm:w-2/5">
              <select
                id="dcf-timezone"
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                className={`${FIELD_CLS} appearance-none pr-6`}
              >
                {TIMEZONES.map((tz) => (
                  <option
                    key={tz.value}
                    value={tz.value}
                    className={OPT_CLS}
                  >
                    {tz.label}
                  </option>
                ))}
              </select>
              {CHEVRON}
            </div>
          </div>
          {!timezoneDetected && (
            <span className={HINT_CLS}>
              Часовой пояс не определён — по умолчанию Москва
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="dcf-comments" className={LABEL_CLS}>
            Комментарий или запрос
          </label>
          <textarea
            id="dcf-comments"
            name="comments"
            value={formData.comments}
            onChange={(e) =>
              setFormData({ ...formData, comments: e.target.value })
            }
            placeholder="Опишите кратко, с чем хотите поработать..."
            rows={3}
            className={`${FIELD_CLS} resize-none`}
          />
        </div>

        <div className="flex flex-col gap-4 mt-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="dcf-privacy"
                checked={formData.privacyAccepted}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    privacyAccepted: e.target.checked,
                  });
                  if (errors.privacyAccepted)
                    setErrors({ ...errors, privacyAccepted: undefined });
                }}
                className={`w-4 h-4 rounded border shrink-0 cursor-pointer ${inv ? "border-white/50 text-white focus:ring-white/30" : "border-foreground/30 text-help focus:ring-help/30"}`}
              />
              <label
                htmlFor="dcf-privacy"
                className={CHECKBOX_LABEL_CLS}
              >
                Согласен с{" "}
                <a
                  href="/privacy"
                  className={LINK_CLS}
                  target="_blank"
                >
                  Политикой конфиденциальности
                </a>{" "}
                (ФЗ №152-ФЗ){" "}
                <span className={REQ_STAR}>*</span>
              </label>
            </div>
            {errors.privacyAccepted && (
              <span className={`${ERR_CLS} pl-7`}>
                {errors.privacyAccepted}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="dcf-marketing"
              checked={formData.marketingAccepted}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  marketingAccepted: e.target.checked,
                })
              }
              className={`w-4 h-4 rounded border shrink-0 cursor-pointer ${inv ? "border-white/50 text-white focus:ring-white/30" : "border-foreground/30 text-help focus:ring-help/30"}`}
            />
            <label
              htmlFor="dcf-marketing"
              className={CHECKBOX_LABEL_CLS}
            >
              Согласен получать полезные материалы и специальные предложения (ФЗ
              №38-ФЗ «О рекламе»)
            </label>
          </div>
        </div>
      </div>

      <div id="dcf-captcha-container" className="hidden" />

      <div className="mt-auto pt-fluid-xl">
        <div className="relative group/submit w-full">
          <button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className={BTN_CLS}
          >
            {isSubmitting ? "Отправка..." : "Оставить заявку"}
          </button>
          {!isFormValid && !isSubmitting && (
            <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-foreground/90 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover/submit:opacity-100 transition-opacity duration-150 pointer-events-none select-none z-10">
              Пожалуйста, заполните форму
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
